import { createContext, useCallback, useContext, useEffect, useId, useMemo, useSyncExternalStore, type JSX, type ReactNode } from 'react';

import { creerMagasinDeBrouillons, type MagasinDeBrouillons } from './brouillons.js';

/** Sans fournisseur (test, atelier) : un magasin en memoire seule, qui ne survit pas a la page. */
const MAGASIN_PAR_DEFAUT = creerMagasinDeBrouillons({ orgId: '', uid: '', stockage: null });
const ContexteDeBrouillons = createContext<MagasinDeBrouillons>(MAGASIN_PAR_DEFAUT);

function stockageDuPoste(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

export interface FournisseurDeBrouillonsProps {
  orgId: string;
  /** La personne connectee. Absente : les brouillons restent en memoire, rien n'est ecrit sur le poste. */
  uid?: string | null | undefined;
  children: ReactNode;
}

/**
 * LE MAGASIN DES BROUILLONS d'une personne dans une organisation, pose UNE fois
 * au-dessus de toutes les surfaces de saisie (apps et centre), pour qu'un meme
 * canal ait le meme brouillon partout. Il est recree quand l'organisation ou la
 * personne change : le brouillon d'une autre personne du poste ne passe jamais.
 */
export function FournisseurDeBrouillons({ orgId, uid, children }: FournisseurDeBrouillonsProps): JSX.Element {
  const magasin = useMemo(
    () => creerMagasinDeBrouillons({ orgId, uid: uid ?? '', stockage: uid ? stockageDuPoste() : null }),
    [orgId, uid],
  );
  // Un autre onglet de la meme personne ecrit : on suit, sans quoi le plus recent efface l'autre.
  useEffect(() => {
    const surStockage = (evenement: StorageEvent): void => {
      if (evenement.storageArea !== null && evenement.storageArea !== stockageDuPoste()) return;
      magasin.surStockageExterne(evenement.key, evenement.newValue);
    };
    window.addEventListener('storage', surStockage);
    return () => window.removeEventListener('storage', surStockage);
  }, [magasin]);
  return <ContexteDeBrouillons.Provider value={magasin}>{children}</ContexteDeBrouillons.Provider>;
}

export function useMagasinDeBrouillons(): MagasinDeBrouillons {
  return useContext(ContexteDeBrouillons);
}

/**
 * LE BROUILLON D'UNE CLE (`cleDeConversation`, `cleDeSujetLia`) : le texte et
 * son setter, comme un `useState`. Le texte change quand la cle change (passer
 * de A a B montre B, revenir a A rend A). Une cle `null` est un brouillon sans
 * conversation identifiable : propre a ce composant, en memoire seule.
 */
export function useBrouillon(cle: string | null): [string, (texte: string | ((courant: string) => string)) => void] {
  const magasin = useMagasinDeBrouillons();
  const identifiant = useId();
  const cleEffective = cle ?? `memoire:${identifiant}`;
  const lire = useCallback(() => magasin.lire(cleEffective), [magasin, cleEffective]);
  const texte = useSyncExternalStore(magasin.ecouter, lire, lire);
  const ecrire = useCallback((suivant: string | ((courant: string) => string)): void => {
    magasin.ecrire(cleEffective, typeof suivant === 'function' ? suivant(magasin.lire(cleEffective)) : suivant);
  }, [magasin, cleEffective]);
  return [texte, ecrire];
}

/** Les cles qui ont un brouillon (pour marquer les listes) ; voir `aUnBrouillon`. */
export function useClesDeBrouillons(): ReadonlySet<string> {
  const magasin = useMagasinDeBrouillons();
  return useSyncExternalStore(magasin.ecouter, magasin.cles, magasin.cles);
}
